import { vec3 } from 'gl-matrix';
import { Scene } from '../Scene';
import { SceneManifest } from '../SceneManifest';
import { RenderObject } from '../../object/RenderObject';
import { GltfAnimation } from '../../animation';
import { IkSystem, discoverLegChains } from '../../animation/ik';
import { PhysicsFeature } from '../../physics';

export function loadObjectPhysics(
  scene: Scene,
  manifest: SceneManifest,
  namedObjects: Map<string, RenderObject[]>,
): void {
  const physics = scene.features.get('physics') as PhysicsFeature | undefined;
  if (!physics) return;

  registerBoneHitDetection(scene, physics, manifest, namedObjects);

  for (const objDef of manifest.globalStaticObjects ?? []) {
    if (!objDef.physics) continue;

    const objects = namedObjects.get(objDef.name);
    if (!objects?.length) continue;

    const def = objDef.physics;
    const primaryObj = objects[0];

    let velocity = vec3.create();
    if (def.initialVelocity) {
      vec3.set(velocity, def.initialVelocity[0], def.initialVelocity[1], def.initialVelocity[2]);
    }

    let angularVelocity: vec3 | undefined;
    if (def.initialAngularVelocity) {
      angularVelocity = vec3.fromValues(
        def.initialAngularVelocity[0],
        def.initialAngularVelocity[1],
        def.initialAngularVelocity[2],
      );
    }

    physics.registerProjectile({
      position: vec3.fromValues(
        primaryObj.position[0] as number,
        primaryObj.position[1] as number,
        primaryObj.position[2] as number,
      ),
      velocity,
      gravity: def.gravity,
      restitution: def.restitution,
      surfaceOffset: def.surfaceOffset,
      mass: def.mass,
      enableRotation: def.enableRotation,
      initialAngularVelocity: angularVelocity,
      angularDamping: def.angularDamping,
      renderObject: primaryObj,
      persist: true,
    });
  }
}

/**
 * Auto-register every animated render object as a bone hit detection target,
 * applying per-bone radius/shape overrides from each model's _BONE_COLLISION
 * GLB extras. Runs whenever the physics feature is enabled, so any consumer
 * of the manifest gets bone hit detection without app-level wiring.
 */
function registerBoneHitDetection(
  scene: Scene,
  physics: PhysicsFeature,
  manifest: SceneManifest,
  namedObjects: Map<string, RenderObject[]>,
): void {
  for (const objDef of manifest.globalStaticObjects ?? []) {
    const objects = namedObjects.get(objDef.name);
    if (!objects?.length) continue;
    const model = scene.modelRegistry.get(objDef.model);
    for (const obj of objects) {
      physics.boneHitDetection.registerAnimated(obj, model?.json);
    }
  }
}

export function setupIk(scene: Scene, namedObjects: Map<string, RenderObject[]>): void {
  for (const objects of namedObjects.values()) {
    for (const obj of objects) {
      if (!obj.animationState || !obj.animation) continue;
      const anim = obj.animation as GltfAnimation;
      const chains = discoverLegChains(anim);
      if (chains.length === 0) continue;

      const ikSystem = new IkSystem(anim, obj.animationState);
      ikSystem.chains = chains;
      ikSystem.volumeGrid = scene.volumeGrid;

      // Estimate foot offset (ankle-to-sole height) from bind pose
      const toeIndex = anim.getJointIndex('LeftToeBase');
      const footIndex = anim.getJointIndex('LeftFoot');
      if (toeIndex !== undefined && footIndex !== undefined) {
        const positions = anim.computeBindPosePositions();
        // Toe Y gives a good approximation of sole height
        const toeY = positions[toeIndex][1];
        const footY = positions[footIndex][1];
        const offset = footY - toeY;
        for (const chain of chains) chain.footOffset = Math.max(0, offset);
      }

      obj.ikSystem = ikSystem;
    }
  }
}
